import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { groupByDay, timeOnly } from "../../lib/slots";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const SlotButton = ({ s, onBook, onCancel, t, lang }) => {
  if (s.mine) return (
    <div className="col-span-full flex items-center gap-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-3 py-2" data-testid={`book-slot-mine-${s.id}`}>
      <CheckCircle2 size={15} className="text-emerald-600" />
      <span className="text-sm font-bold text-emerald-800">{timeOnly(s.start, lang)} · {t(`sl_${s.kind}`)}</span>
      <button onClick={() => onCancel(s)} data-testid={`book-slot-cancel-${s.id}`} className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-red-600">{t("sl_cancel")}</button>
    </div>
  );
  return (
    <button onClick={() => onBook(s)} disabled={s.booked} data-testid={`book-slot-${s.id}`}
      className="rounded-xl border border-slate-200 px-3 py-2 text-left hover:border-[#F5551E] hover:bg-[#FFF4F0] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-slate-200 transition-colors">
      <span className="text-sm font-bold text-[#12234A]">{timeOnly(s.start, lang)}</span>
      <span className="block text-[10px] text-slate-500">{t(`sl_${s.kind}`)} · {s.duration_min} {t("sl_min")}{s.booked ? ` · ${t("sl_taken")}` : ""}</span>
    </button>
  );
};

export function BookSlotDialog({ litter, onClose }) {
  const { t, lang } = useLanguage();
  const [slots, setSlots] = useState(null);
  const load = () => api.get(`/litters/${litter.id}/slots`).then((r) => setSlots(r.data)).catch((e) => { toast.error(errText(e)); setSlots([]); });
  useEffect(() => { if (litter) { setSlots(null); load(); } }, [litter]); // eslint-disable-line react-hooks/exhaustive-deps
  const book = async (s) => {
    try { await api.post(`/litter-slots/${s.id}/book`); toast.success(t("sl_booked_toast")); load(); } catch (e) { toast.error(errText(e)); load(); }
  };
  const cancel = async (s) => {
    try { await api.delete(`/litter-slots/${s.id}/book`); toast.success(t("sl_cancel_toast")); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="book-slot-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("sl_book_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("sl_book_desc")}</DialogDescription>
        </DialogHeader>
        {slots === null ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#F5551E]" /></div>
          : slots.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="book-slot-empty">{t("sl_no_open")}</p>
          : groupByDay(slots, lang).map((g) => (
            <div key={g.day}>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">{g.day}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {g.items.map((s) => <SlotButton key={s.id} s={s} onBook={book} onCancel={cancel} t={t} lang={lang} />)}
              </div>
              {[...new Set(g.items.map((s) => s.note).filter(Boolean))].map((n) => <p key={n} className="text-[11px] text-slate-500 mt-1.5">{n}</p>)}
            </div>
          ))}
      </DialogContent>
    </Dialog>
  );
}
